import './productPage.scss';
import { FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Message } from 'tedooo-web-design-system/ui';
import classNames from 'classnames';
import { ArrowLeft, HelpCircle, MoreVertical } from 'tedooo-web-design-system/icons';
import { connect } from 'react-redux';

import {
  DiscoverListTypes,
  IDiscoverListDetails,
  IProductPage,
  IStore2,
  MenuOptions,
  popupIdsOptions,
} from 'types';
import DropdownOptionsMenu from 'components/dropdownOptionsMenu/DropdownOptionsMenu';
import { productDropdownOptions } from './helper';
import ExpandableText from 'components/expandableText/ExpandableText';
import ShippingAndPoliciesSection from './shippingAndPoliciesSection/ShippingAndPoliciesSection';
import SellerBox from './sellerBox/SellerBox';
import {
  getSpecificProduct,
  onProductLikeToggle,
} from 'store/specificProduct/SpecificProductActions';
import { translations } from 'translations/en';
import { ROUTES } from 'constant';
import { onFollowUserToggle } from 'store/profile/ProfileActions';
import { SpecificProductSlicer } from 'store/specificProduct/SpecificProductSlicer';
import { UserSelector } from 'store/user/UserSelector';
import { PopupSlicer } from 'store/popup/PopupActions';
import { CreateShopSlicer } from 'store/createShop/CreateShopSlicer';
import { SpecificProductSelector } from 'store/specificProduct/SpecificProductSelector';
import { getShippingAddresses as getShippingAddressesAction } from 'store/settings/SettingsActions';
import { buildChatUrl, buildDiscoverPageQuery, toDateFormatWithText } from 'utils';
import Tip from 'components/tip/Tip';
import ProductsCarousel from '../discoverPage/modularComponents/productsCarousel/ProductsCarousel';
import { FULL_WIDTH_CAROUSEL_SLIDES } from '../discoverPage/helper';
import { AnalyticsEvents } from 'services/AnalyticsEvents';
import { SettingsSelector } from 'store/settings/SettingsSelector';
import ShopReviewsCarousel from './shopReviewsCarousel/ShopReviewsCarousel';
import ProductDetailsWrapper from './ProductDetailsWrapper';
import { useRequireToken } from 'hooks/useRequireToken';

const { TITLE } = translations.SPECIFIC_PRODUCT.SELLER_BOX;

const ProductPage: FC<IProductPage> = ({
  token,
  isLoading,
  userId,
  shopId,
  tip,
  vacationDetails,
  product,
  reviews,
  totalReviews,
  recipientCountry,
  isFollowing,
  userDetails,
  averageRating,
  shopName,
  isShopOwner,
  getProduct,
  onFollow,
  getShippingAddresses,
  reset,
  editListing,
  productCarousels,
  showPopup,
  onLikeProduct,
}) => {
  const { shippingInformation, description } = product;

  const params = useParams();

  const navigate = useNavigate();

  const { fullName, avatar, username } = userDetails;

  const productId = params?.productId;

  useEffect(() => {
    if (productId) {
      getProduct({ productId });
    }
    if (!recipientCountry && token) {
      getShippingAddresses();
    }

    return () => {
      reset();
    };
  }, [productId]);

  const onReportClick = useRequireToken(() => {
    showPopup({ id: popupIdsOptions.reportUserPopup, userId });
  });

  const onOptionClick = (type: MenuOptions) => {
    if (type === MenuOptions.GoToShop) {
      onGoToShopClick();
    }
    if (type === MenuOptions.Message) {
      onMessageSellerClick();
    }
    if (type === MenuOptions.Report) {
      onReportClick();
    }
    if (type === MenuOptions.Edit) {
      onEditClick();
    }
    if (type === MenuOptions.Delete) {
      showPopup({ id: popupIdsOptions.deleteItemPopup, productId: productId || '' });
    }
  };

  const dropdownOptions = productDropdownOptions.filter(
    (option) =>
      (option.key !== MenuOptions.Edit || isShopOwner) &&
      (option.key !== MenuOptions.Delete || isShopOwner) &&
      (option.key !== MenuOptions.GoToShop || !isShopOwner) &&
      (option.key !== MenuOptions.Message || !isShopOwner) &&
      (option.key !== MenuOptions.Report || !isShopOwner),
  );

  const onGoToShopClick = () => {
    navigate(`/${ROUTES.SHOP}/${shopId}`);
  };

  const onMessageSellerClick = useRequireToken(() => {
    const chatDetails = {
      userId,
      username,
      avatar,
      fullName,
    };

    const url = buildChatUrl(chatDetails);
    navigate(url);
  });

  const onEditClick = () => {
    navigate(`/${ROUTES.EDIT_SHOP}`);
    editListing({ id: product.id });
  };

  const onFollowingClick = useRequireToken(() => {
    onFollow({ userId, isFollowing });
  });

  const onBackClick = () => {
    navigate(-1);
  };

  const onLikeClick = useRequireToken(() => {
    onLikeProduct({ productId: product.id, isLiked: product.isLiked, source: 'products' });
  });

  const onProductClick = (id: string) => {
    navigate(`/${ROUTES.PRODUCT}/${id}`);
  };

  const onListClick = (options: IDiscoverListDetails) => {
    const { listId, title: productPageTitle, paginationToken, type } = options;

    if (type === DiscoverListTypes.Products) {
      AnalyticsEvents.onProductsPageClick('product', listId); // check if this is the right event

      const query = buildDiscoverPageQuery({
        id: listId,
        paginationToken,
        title: productPageTitle,
      });
      navigate(`/${ROUTES.PRODUCTS}${query}`);
    }
  };

  const renderVacationMessage = () => {
    return (
      <>
        <span>{translations.SETTINGS.POPUPS.VACATION_MODE_SHOP_MESSAGE}</span>
        <span className="shop-message">{vacationDetails?.text}</span>
        <span>Last updated: {toDateFormatWithText(vacationDetails?.date || 0)}</span>
      </>
    );
  };

  return (
    <div className="page product-page-container">
      <div className="product-page-header">
        <Button
          type="secondary"
          size="medium"
          borderRadius="small"
          onClick={onBackClick}
          prefixIcon={<ArrowLeft />}
          text={translations.COMMON.BACK}
        />
        <DropdownOptionsMenu options={dropdownOptions} onOptionClick={onOptionClick}>
          <Button
            type="secondary"
            size="medium"
            disabled={isLoading}
            borderRadius="small"
            prefixIcon={<MoreVertical />}
            className="more-button"
          />
        </DropdownOptionsMenu>
      </div>
      <ProductDetailsWrapper shopId={shopId} onEdit={onEditClick} onLikeToggle={onLikeClick} />
      {isShopOwner && tip && <Tip tip={tip} />}
      {!isShopOwner && vacationDetails && (
        <Message
          message={renderVacationMessage()}
          type="primary"
          prefixIcon={<HelpCircle />}
          className="vacation-message"
        />
      )}
      {!isLoading && (
        <div className="item-details-container">
          <span className="title">Item Details</span>
          <ExpandableText
            text={description || ''}
            maxChars={262}
            isCollapsable
            isTextClickable={false}
          />
        </div>
      )}
      <div className="policies-and-seller-info-container">
        {shippingInformation && (
          <ShippingAndPoliciesSection shippingInformation={shippingInformation} />
        )}
        <div className={classNames('right-side', { 'is-digital': !shippingInformation })}>
          {!isLoading && !isShopOwner && (
            <SellerBox
              avatar={avatar}
              title={TITLE}
              name={fullName}
              shopName={shopName}
              isFollowing={isFollowing}
              onUserClick={onGoToShopClick}
              onMessageSellerClick={onMessageSellerClick}
              onFollowingClick={onFollowingClick}
            />
          )}
          {reviews && !isLoading && (
            <ShopReviewsCarousel
              average={averageRating}
              totalReviews={totalReviews}
              reviews={reviews}
            />
          )}
        </div>
      </div>
      {productCarousels?.map((carousel, i) => (
        <ProductsCarousel
          listDetails={carousel.listDetails}
          products={carousel.products}
          titles={carousel.titles}
          marginTop={carousel.marginTop}
          slidesToShow={FULL_WIDTH_CAROUSEL_SLIDES}
          onLikeClick={onLikeClick}
          onProductClick={onProductClick}
          onListClick={onListClick}
          key={i}
        />
      ))}
    </div>
  );
};

const mapStateToProps = (state: IStore2) => {
  const { userId, shopId, shopDetails, vacationDetails, isLoading, productCarousels } =
    state.specificProduct;

  const product = SpecificProductSelector.getProductSelector(state);

  const tip = SpecificProductSelector.getTipSelector(state);

  const loggedUserId = UserSelector.loggedUserIdSelector(state);

  const token = UserSelector.tokenSelector(state);

  const isShopOwner = userId === loggedUserId;

  const recipientCountry = SettingsSelector.defaultShippingAddressSelector(state)?.country || null;

  const {
    isFollowing,
    userDetails,
    reviews,
    name: shopName,
    totalReviews,
    averageRating,
  } = shopDetails || {
    isFollowing: false,
    userDetails: { id: '', fullName: '', avatar: '', username: '' },
    name: '',
    country: '',
    reviews: [],
    connectedPayouts: null,
    totalReviews: 0,
    averageRating: 0,
  };

  return {
    shopId,
    product,
    isShopOwner,
    vacationDetails,
    isFollowing,
    userDetails,
    shopName,
    userId,
    recipientCountry,
    reviews,
    totalReviews,
    productCarousels,
    isLoading,
    tip,
    averageRating,
    token,
  };
};

const mapDispatchToProps = {
  onFollow: onFollowUserToggle,
  getProduct: getSpecificProduct,
  showPopup: PopupSlicer.actions.show,
  reset: SpecificProductSlicer.actions.reset,
  editListing: CreateShopSlicer.actions.navigateToEditListingForm,
  onLikeProduct: onProductLikeToggle,
  getShippingAddresses: getShippingAddressesAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductPage);
